import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

import { changePrimarySpotIndex } from 'actions/multiCam';
import { CloseIcon, RailIcon } from 'components/Icons';
import CamAdPlayer from 'components/CamAdPlayer';
import CamTimeout from 'components/CamPlayerV2/CamTimeout';
import LoadingSpinner from 'components/CamPlayerKbygRewinds/LoadingSpinner/LoadingSpinner';
import CamHeaderCTA from 'components/multi-cams/CamHeaderCTA';
import MultiCamPaywall from 'components/MultiCamPaywall';
import PlayButtonOverlay from 'components/PlayButtonOverlay';
import VideoJsAdScripts from 'components/VideoJsAdScripts';
import useAdPlayback from 'components/CamAdPlayer/useAdPlayback';
import { useKeyPress } from 'hooks/useKeyPress';
import { useToggle } from 'hooks/useToggle';
import useVideoJSAds from 'hooks/useVideoJSAds';
import useCamAdVastTag from 'hooks/useCamAdVastTag';
import { getMultiCam } from 'selectors/multiCam';
import { useUserPermissionStatus } from 'selectors/user';
import createAccessibleOnClick, { BTN } from 'utils/createAccessibleOnClick';

import CamControls from './components/CamControls';
import Cam from './components/Cam';
import { useSaveMultiCamState } from './hooks/useSaveMultiCamState';
import { useCamTimeout } from './hooks/useCamTimeout';
import { createFillers, multiCamClass, multiCamContainerClass, surfCamsParentClass } from './utils';

import styles from './MultiCam.module.scss';

interface MultiCamProps {
  canMountCams: boolean;
  isFullscreen: boolean | undefined;
  timeoutTime: number;
  toggleFullscreen: any;
  toggleRightRail: any;
  rightRailIsOpen: boolean;
  children: any;
  multiCamRef: any;
}

const useRedux = () => {
  const dispatch = useDispatch();
  const doChangePrimarySpotIndex = useCallback(
    (camIndex: number) => dispatch(changePrimarySpotIndex(camIndex)),
    [dispatch],
  );
  return { doChangePrimarySpotIndex };
};

const MultiCam = ({
  timeoutTime = 1200000,
  isFullscreen,
  toggleFullscreen,
  rightRailIsOpen = false,
  toggleRightRail,
  children = null,
  multiCamRef,
  canMountCams = true,
}: MultiCamProps) => {
  useSaveMultiCamState();

  /* Cam Container Ref */
  const camsRef = useRef();

  /** State */
  const { numCams, selectedSpots, primarySpotIndex } = useSelector(getMultiCam, shallowEqual);
  const { hasMultiCamPermissions, hasAdFreeCamPermissions, hasAdSupportedCamPermissions } =
    useUserPermissionStatus();

  const [initialPlayTriggered, setInitialPlayTriggered] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  /** Actions */
  const { doChangePrimarySpotIndex } = useRedux();

  /**
   * Memoized array of selected spot ID's `selectedSpots` should be immutable since it is in redux
   * so it is safe to use as a dependency in `useMemo`
   */
  const selectedSpotIds = useMemo(
    () => selectedSpots.map((spot: any) => spot?._id),
    [selectedSpots],
  );

  /** Control the cams playing states */
  const [isPlaying, setPlaying, togglePlaying] = useToggle(
    hasAdFreeCamPermissions || initialPlayTriggered,
  );

  /** Control the ad playing states */
  const firstCamSpot = selectedSpots.find((spot: any) => spot?.cameras?.length);

  const [timeout, resetTimeout] = useCamTimeout({
    setPlaying,
    isPlaying: isPlaying as boolean,
    selectedSpotIds,
    timeoutTime,
    hasCam: !!firstCamSpot,
  });

  const spotsToRender = useMemo(
    () => createFillers(selectedSpots, numCams),
    [numCams, selectedSpots],
  );

  // Allow users to select primary cam using 1, 2, 3, 4, etc...
  const primaryCamSelectionKeys = spotsToRender.map((_, index) => `${index + 1}`);
  useKeyPress(
    useCallback(
      (key: string) => doChangePrimarySpotIndex(parseInt(key, 10) - 1),
      [doChangePrimarySpotIndex],
    ),
    ...primaryCamSelectionKeys,
  );

  const segmentPropertiesCTA = useMemo(
    () => ({
      location: 'Multicam paywall',
      category: 'cams & reports',
      pageName: 'Multicam',
    }),
    [],
  );

  const showPaywall = !hasMultiCamPermissions;

  const [isCamFullscreen, setIsCamFullscreen] = useState(false);

  const timeoutOnClickHandler = useCallback(() => {
    if (typeof resetTimeout === 'function') {
      resetTimeout();
    }
  }, [resetTimeout]);

  const { onVideoJsGoogleImaLoaded, onVideoJsGoogleImaError, videoAdPluginsLoaded } =
    useVideoJSAds();

  const [livePlaying, setLivePlaying] = useState(false);

  const { setPlayAd, adRunning, setAdRunning } = useAdPlayback({
    disableAds: hasAdFreeCamPermissions,
    hasAdSupportedCamPermissions,
    isNonPremiumPlaylist: false,
    pause: () => {},
    play: () => {},
    playerPlaying: livePlaying,
  });

  const { adVastUrl, adTagLoading } = useCamAdVastTag(hasAdFreeCamPermissions);

  const adSettings = useMemo(
    () => ({
      adVastTagUrl: adVastUrl,
    }),
    [adVastUrl],
  );

  const adsPluginLoaded = (!adTagLoading && videoAdPluginsLoaded) || hasAdFreeCamPermissions;

  useEffect(() => {
    if (isPlaying) {
      setInitialPlayTriggered(true);
    }
  }, [isPlaying]);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const showInitialPlayOverlay =
    hasAdSupportedCamPermissions &&
    !hasAdFreeCamPermissions &&
    !initialPlayTriggered &&
    adsPluginLoaded &&
    hasMultiCamPermissions &&
    !adRunning;

  return (
    <div className={styles.multiCam}>
      <div className={surfCamsParentClass(numCams)}>
        <div
          className="sl-multi-cam__rail-toggle"
          {...createAccessibleOnClick(toggleRightRail, BTN)}
        >
          {rightRailIsOpen ? (
            <CloseIcon />
          ) : (
            <>
              <RailIcon />
              <span>spots</span>
            </>
          )}
        </div>
        <div ref={multiCamRef}>
          <div className={multiCamContainerClass(true, numCams, isCamFullscreen)}>
            {isMounted && !adsPluginLoaded && hasMultiCamPermissions && <LoadingSpinner />}
            {hasAdSupportedCamPermissions && !hasAdFreeCamPermissions && (
              <div
                className={classNames({
                  [styles.hidden]: !adRunning,
                  [styles.camAdPlayer]: true,
                })}
              >
                <VideoJsAdScripts
                  hasAdFreeCamPermissions={hasAdFreeCamPermissions}
                  onVideoJsGoogleImaLoaded={onVideoJsGoogleImaLoaded}
                  onVideoJsGoogleImaError={onVideoJsGoogleImaError}
                />
                <CamAdPlayer
                  adSettings={adSettings}
                  setPlayAd={setPlayAd}
                  hasAdFreeCamPermissions={hasAdFreeCamPermissions}
                  setAdRunning={setAdRunning}
                  videoAdPluginsLoaded={videoAdPluginsLoaded}
                  segmentScreen="multicam"
                />
              </div>
            )}
            <div
              // @ts-ignore
              ref={camsRef}
              className={multiCamClass(
                numCams,
                // @ts-ignore
                timeout,
                showPaywall,
              )}
            >
              {isMounted && showInitialPlayOverlay && (
                <PlayButtonOverlay visible onClick={togglePlaying} />
              )}
              {isMounted &&
                adsPluginLoaded &&
                hasMultiCamPermissions &&
                spotsToRender?.map((spot, camIndex) => (
                  <Cam
                    // Since we can have the same cam in different cam players we have to use an index as a key
                    key={`${spot?._id || ''}${camIndex}`}
                    // @ts-ignore
                    spot={spot}
                    isPlaying={isPlaying}
                    camIndex={camIndex}
                    primarySpotIndex={primarySpotIndex}
                    onClick={() => doChangePrimarySpotIndex(camIndex)}
                    canMountCams={canMountCams}
                    camsRef={camsRef}
                    showingPaywall={showPaywall}
                    isParentFullscreen={isFullscreen}
                    setIsCamFullscreen={setIsCamFullscreen}
                    setLivePlaying={setLivePlaying}
                    autoPlay={hasAdFreeCamPermissions}
                  />
                ))}
              <CamTimeout onClick={timeoutOnClickHandler} />
              {showPaywall && <MultiCamPaywall segmentProperties={segmentPropertiesCTA} />}
            </div>
          </div>
          <CamControls
            isFullscreen={isFullscreen}
            isPlaying={isPlaying}
            toggleFullscreen={toggleFullscreen}
            togglePlaying={togglePlaying}
          />
        </div>
        {!hasMultiCamPermissions && (
          <CamHeaderCTA segmentProperties={{ location: 'button top - multicam' }} />
        )}
        {children}
      </div>
    </div>
  );
};

export default MultiCam;
